import {
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	TextArea,
} from "@dune/ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	defaultFormValues,
	eventOptions,
	normalizePayload,
	webhookFormSchema,
	type WebhookFormValues,
} from "../formSchema";

export interface WebhookFormDialogProps {
	open: boolean;
	mode: "create" | "edit";
	initialValues: WebhookFormValues;
	onClose: () => void;
	onSubmit: (
		payload: ReturnType<typeof normalizePayload>,
	) => Promise<void> | void;
	isSubmitting: boolean;
}

export const WebhookFormDialog = ({
	open,
	mode,
	initialValues,
	onClose,
	onSubmit,
	isSubmitting,
}: WebhookFormDialogProps) => {
	const form = useForm<WebhookFormValues>({
		resolver: zodResolver(webhookFormSchema),
		defaultValues: defaultFormValues,
	});

	useEffect(() => {
		if (open) {
			form.reset(initialValues);
		}
	}, [form, initialValues, open]);

	const handleSubmit = form.handleSubmit(async (values) => {
		await onSubmit(normalizePayload(values));
	});

	return (
		<Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
			<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>
						{mode === "create" ? "Nouveau webhook" : "Modifier le webhook"}
					</DialogTitle>
					<DialogDescription>
						Configurez l'URL cible, les évènements écoutés et l'état du webhook.
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={handleSubmit} className="space-y-6">
						<div className="grid gap-4 md:grid-cols-2">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Nom</FormLabel>
										<FormControl>
											<Input {...field} placeholder="CRM Hubspot" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="url"
								render={({ field }) => (
									<FormItem>
										<FormLabel>URL cible</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder="https://example.com/webhooks"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Description (optionnel)</FormLabel>
									<FormControl>
										<TextArea
											{...field}
											placeholder="Synchronisation des utilisateurs vers notre CRM"
											rows={3}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="isActive"
							render={({ field }) => (
								<FormItem>
									<div className="flex items-start gap-3 rounded-md border p-3">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={(checked) => field.onChange(!!checked)}
											/>
										</FormControl>
										<div>
											<p className="font-medium">Webhook actif</p>
											<p className="text-sm text-muted-foreground">
												Désactivez temporairement le webhook sans supprimer la
												configuration.
											</p>
										</div>
									</div>
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="events"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Évènements emis</FormLabel>
									<FormDescription>
										Sélectionnez les évènements à transmettre a ce webhook.
									</FormDescription>
									<div className="mt-3 space-y-3">
										{eventOptions.map((option) => {
											const inputId = `webhook-event-${option.value}`;
											const isChecked =
												field.value?.includes(option.value) ?? false;
											return (
												<div
													key={option.value}
													className="flex items-start gap-3 rounded-md border p-3"
												>
													<FormControl>
														<Checkbox
															id={inputId}
															checked={isChecked}
															onCheckedChange={(checked) => {
																const next = checked
																	? [...(field.value ?? []), option.value]
																	: (field.value ?? []).filter(
																			(value) => value !== option.value,
																		);
																field.onChange(next);
															}}
														/>
													</FormControl>
													<label
														htmlFor={inputId}
														className="cursor-pointer space-y-1"
													>
														<p className="font-medium">{option.label}</p>
														<p className="text-sm text-muted-foreground">
															{option.description}
														</p>
													</label>
												</div>
											);
										})}
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						<DialogFooter>
							<Button type="button" variant="outline" onClick={onClose}>
								Annuler
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting
									? "Enregistrement..."
									: mode === "create"
										? "Creer le webhook"
										: "Mettre a jour"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};
